/**
 * HomeFAQTeaser — Kompakte 3-Spalten-Sektion für die Startseite.
 *
 * Layout: Links Titel + Beschreibung, Mitte Liste ausklappbarer Zeilen
 * (Icon + Frage + Chevron), Rechts der große gelbe "Alle FAQ ansehen"-Button
 * mit Link auf `/faq`. Klick auf eine Zeile öffnet ein Modal mit der
 * vollständigen Antwort.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { ArrowRight, X, ChevronDown } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DynamicIcon = ({ name, className }) => {
  const Icon = Icons[name] || Icons.HelpCircle;
  return <Icon className={className} />;
};

const HomeFAQTeaser = () => {
  const [faqs, setFaqs] = useState([]);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/public/faqs`, { params: { featured: true } })
      .then((res) => {
        if (!cancelled) setFaqs(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  if (!faqs.length) return null;

  return (
    <section
      className="py-6 md:py-10"
      data-testid="home-faq-teaser"
      style={{ background: 'linear-gradient(180deg, #eaf4ff 0%, #d8ecff 100%)' }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-[minmax(220px,260px)_1fr_auto] gap-6 md:gap-8 items-center">
          {/* Left: Title + Description */}
          <div className="space-y-2.5">
            <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-sky-700/80">
              Häufige Fragen
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-sky-900 leading-[1.1]">
              Fragen &amp; Antworten
            </h2>
            <p className="text-xs sm:text-sm text-sky-900/60 leading-relaxed max-w-xs">
              Schnelle Antworten rund um Buchung, Lieferung, Aufbau und mehr.
            </p>
          </div>

          {/* Middle: FAQ list */}
          <ul className="space-y-2.5" data-testid="home-faq-list">
            {faqs.map((faq) => (
              <li key={faq.id}>
                <button
                  type="button"
                  onClick={() => setOpenFaq(faq)}
                  data-testid={`home-faq-row-${faq.id}`}
                  className="w-full flex items-center gap-3 bg-white rounded-full pl-3 pr-4 py-2.5 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all text-left group"
                >
                  <span className="shrink-0 h-9 w-9 rounded-xl bg-sky-50 text-sky-600 inline-flex items-center justify-center">
                    <DynamicIcon name={faq.icon} className="h-5 w-5" />
                  </span>
                  <span className="flex-1 min-w-0 text-[14px] sm:text-[15px] font-semibold text-sky-900 truncate">
                    {faq.question}
                  </span>
                  <ChevronDown className="shrink-0 h-4 w-4 text-sky-500 transition-transform group-hover:translate-y-0.5" />
                </button>
              </li>
            ))}
          </ul>

          {/* Right: CTA */}
          <div className="md:pl-2">
            <Link
              to="/infos-faq"
              className="inline-flex items-center gap-2 bg-yellow-400 hover:bg-yellow-500 text-sky-900 font-bold rounded-full px-5 sm:px-6 py-3 text-sm shadow-md transition-transform hover:-translate-y-0.5 whitespace-nowrap"
              data-testid="home-faq-cta-all"
            >
              Alle FAQ ansehen
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>

      {openFaq && <FAQAnswerModal faq={openFaq} onClose={() => setOpenFaq(null)} />}
    </section>
  );
};

// -------- Modal --------
const FAQAnswerModal = ({ faq, onClose }) => {
  useEffect(() => {
    const handler = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={onClose}
      data-testid="home-faq-modal"
    >
      <div
        className="w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 px-6 py-5 bg-gradient-to-r from-sky-500 to-sky-600 text-white">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-10 w-10 rounded-xl bg-white/20 inline-flex items-center justify-center shrink-0">
              <DynamicIcon name={faq.icon} className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              {faq.category && (
                <div className="text-[10px] font-bold uppercase tracking-widest text-sky-100">
                  {faq.category}
                </div>
              )}
              <h3 className="font-bold text-lg leading-tight break-words">{faq.question}</h3>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="h-9 w-9 rounded-full bg-white/20 hover:bg-white/30 inline-flex items-center justify-center shrink-0"
            aria-label="Schließen"
            data-testid="home-faq-modal-close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-6">
          <p className="text-[15px] text-neutral-700 leading-relaxed whitespace-pre-line">
            {faq.answer || faq.short_answer}
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            <Link
              to="/infos-faq"
              onClick={onClose}
              className="inline-flex items-center gap-2 bg-sky-600 hover:bg-sky-700 text-white font-semibold rounded-full px-5 py-2.5 text-sm transition-colors"
              data-testid="home-faq-modal-all"
            >
              Alle FAQ ansehen
              <ArrowRight className="h-4 w-4" />
            </Link>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center rounded-full px-5 py-2.5 text-sm font-semibold text-sky-700 hover:bg-sky-50 transition-colors"
            >
              Schließen
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomeFAQTeaser;
